import type { HarnessUiContext } from '@flowx/designer/modules/ai-builder/services/harness-ui-context.service'
import { HarnessSentFile } from '@flowx/designer/modules/ai-builder/types/conversation-file.types'
import {
  HarnessExecutionPreview,
  HarnessInterrupt,
  HarnessInterruptType,
  HarnessSessionRecord,
  HarnessStoryPlanSnapshot,
  HarnessStoryState,
} from '@flowx/designer/modules/ai-builder/types/harness.types'

/** `blocked` is the tool that stopped planning (`planning_blocked`): not a failure of the
 * call, a finding that the change cannot be planned — amber, never a green tick. */
export type HarnessToolStatus = 'running' | 'done' | 'recoverable-error' | 'blocked'

/** A review artefact the harness proposed: the one-pager (brief) or the plan. */
export type HarnessArtefact = 'brief' | 'plan'

/** Which document each interrupt puts under review. A table rather than a switch, so an interrupt
 * type added later fails the build until someone says what it reviews. */
const ARTEFACT_UNDER_REVIEW: Readonly<Record<HarnessInterruptType, HarnessArtefact | null>> = {
  ask_user: null,
  brief_review: 'brief',
  plan_review: 'plan',
  direct_review: 'plan',
  fix_review: null,
  ship_review: null,
  specialist_review: null,
  read_grant: null,
  retarget_review: null,
  merge_conflict: null,
}

/** The document the open interrupt asks the maker to review, null when it asks for something else
 * or when no interrupt is open. */
export function artefactUnderReview(
  interruptType: HarnessInterruptType | null
): HarnessArtefact | null {
  return interruptType ? ARTEFACT_UNDER_REVIEW[interruptType] : null
}

/** Something the maker settled without writing: an option picked, a decision flipped, something
 * ruled out, a question answered. Recorded as a check-line under the turn that asked rather than
 * as a message, so "You" turns stay what the maker actually wrote. Same `respond` on the wire. */
export type HarnessAnswer = { subject: string; text: string; question?: string }

/** Answers without a subject are numbered when appended to the transcript. */
export type HarnessAnswerInput = Omit<HarnessAnswer, 'subject'> & { subject?: string }

/** What became of a turn the maker sent: the harness took it (its stream opened), refused it before it started, could not be reached, or it was never sent. */
export type HarnessTurnAdmission =
  | { kind: 'admitted' }
  | { kind: 'refused'; status: number; message: string }
  | { kind: 'failed'; message: string }
  | { kind: 'blocked'; reason: string }

/** The component a comment turn points at, as its user turn shows it. */
export type HarnessTurnComment = {
  /** The component's name as the comment box showed it; its type's label after a reload. */
  component: string
  /** The UI flow or process the run is of: its name when sent, its kind after a reload. */
  page: string
  /** False when the harness did not read the comment's context (stored status other than `available`). */
  contextRead: boolean
  /** The component is defined by another template or process than the page's and the turn did not
   * say which. Not kept across a reload. */
  ownerMissing?: true
}

export type HarnessTurnOptions = {
  /** Replaces, whole, the ui_context captured from the page. */
  uiContext?: HarnessUiContext
  comment?: HarnessTurnComment
}

export type HarnessTranscriptEntry =
  /** `files` are the ones the message carried: as this tab sent them, or read back from the
   * session's history after a reload. */
  /** `notRun` marks a message whose turn never ran — refused as a whole, or lost before its first
   * frame: its files stay on it, but none of them counts as sent. */
  | {
      type: 'user'
      text: string
      at: number
      files?: readonly HarnessSentFile[]
      notRun?: boolean
      comment?: HarnessTurnComment
    }
  /** `question` is what was asked, kept because the interrupt that showed it closes on the answer. */
  | { type: 'answer'; subject: string; text: string; question?: string; at: number }
  /** `messageId` is what the harness files a verdict against. It is absent on a turn that has only
   * streamed — the SSE frames carry no ids — and is stamped by `withMessageIds` once the session
   * has been re-read. Carried by the three types that stand for something the agent said; a notice
   * cannot, because some of those are written here rather than received. */
  | { type: 'assistant'; text: string; streaming: boolean; at: number; messageId?: string }
  /**
   * The agent stopped and asked. Renders nothing — the interrupt below the transcript shows the
   * question — but it holds the place where the agent spoke, and that place is load-bearing twice
   * over: it is what makes a question-only turn rateable, and the harness files a verdict against
   * `ask_user` and `interrupt` like any other message, so an interrupt leaving no entry would hand
   * the next reply somebody else's id.
   */
  | { type: 'interrupt'; at: number; messageId?: string }
  | {
      type: 'tool'
      toolUseId: string
      /** The label when the harness supplied one, else the raw tool name. */
      name: string
      /** True when `name` is a raw tool name, so the row can render it as code. */
      isRawName: boolean
      status: HarnessToolStatus
      resultSummary?: string
      at: number
    }
  | {
      type: 'artefact-marker'
      artefact: HarnessArtefact
      revision: number
      at: number
      /** Set on a brief that answers the maker's note on the one before, and on every plan after
       * the first — a plan is only ever re-proposed on the maker's word. */
      revisedOnNote?: true
      /** The brief's title when it landed. The store keeps only the latest brief, so without
       * this every chip in the rail would be relabelled with the newest title. */
      title?: string
      /** As on `assistant`. */
      messageId?: string
    }
  | {
      type: 'notice'
      level: 'info' | 'error'
      code?: string
      text: string
      /** The lines under the sentence — a `planning_failed`'s hard failures. */
      details?: readonly string[]
      /** What the maker can do about it, in the harness's own words. */
      action?: string
      /** The harness says the same request may go through if sent again. */
      retryable?: boolean
    }
  /** The run's progress as it stood at its latest event. Run events update it in place until the
   * maker writes or answers; the next run event then opens a new one below, so each keeps the
   * progress the maker saw when they spoke. `stories` is that progress, for the run it names. */
  | { type: 'run'; runId: string; stories: readonly HarnessStoryRunEntry[] }

/**
 * `awaiting-answer` is deliberately distinct from `finished`: both are streams that stopped, but
 * only one of them is waiting on the maker. Collapsing them is how a UI ends up showing a spinner
 * forever or hiding a question nobody answers. `blocked` is a turn that ended in
 * `planning_failed` — nothing broke and nothing is outstanding, but the maker owes a new
 * instruction rather than an answer. `interrupted` is a turn that never reached its end: nothing
 * failed and nothing is owed but a retry.
 */
export type HarnessStreamState =
  'idle' | 'streaming' | 'awaiting-answer' | 'finished' | 'blocked' | 'interrupted' | 'error'

/** The notice code the transcript hangs its Retry on. */
export const INTERRUPTED_NOTICE = 'interrupted'

/** What the maker's retarget answer says in the conversation, live and when it is restored. */
export const RETARGET_MOVE_ANSWER = 'Move the whole plan.'
export const RETARGET_KEEP_ANSWER = 'Keep the plan here.'

/** One progress row per run/story, shared by live events and durable snapshot recovery. */
export type HarnessStoryRunEntry = {
  runId: string
  storyId: string
  name: string | null
  state: HarnessStoryState | null
  commitId: string | null
}

export type HarnessSessionState = {
  /** The whole chosen story snapshot. Never filled field-by-field from another source. */
  storyPlanSnapshot: HarnessStoryPlanSnapshot | null
  storyPlanProposal: {
    planId: string | null
    version: number | null
    recordRevision: number
  } | null
  storyRunEntries: readonly HarnessStoryRunEntry[]
  /** The run events already folded, so a replay on reconnect neither repeats nor moves anything. */
  runEventKeys: readonly string[]
  /** The plans a run has started on. The harness only parks a brief change for such a plan. */
  runStartedPlanIds: readonly string[]
  sessionId: string | null
  transcript: readonly HarnessTranscriptEntry[]
  /** Calls still awaiting a result, ordered by start time. Kept separately from the transcript so
   * the activity caption does not rescan the full conversation for every streamed token. */
  runningTools: readonly { toolUseId: string; name: string }[]
  streamState: HarnessStreamState
  interrupt: HarnessInterrupt | null
  /** 0 until the first plan. Incremented per `plan_proposed`, which is what makes a revised
   * plan read as a revision rather than a second plan. */
  planRevision: number
  /** The interrupt payload as it arrived. Null until one has. */
  latestPlan: unknown
  /** The brief is a separate artefact from the plan, and a session can produce both. */
  briefRevision: number
  /** As `latestPlan`. Kept for the life of the session so the rail's chips can reopen it. */
  latestBrief: unknown

  /**
   * The maker answered a brief interrupt with a note, so the next brief is a revision on it. Kept apart
   * from `rev`, which the harness counts per session rather than per note: a fresh request on a
   * session that already produced three briefs arrives as `rev: 4`.
   */
  briefRevisionPending: boolean
  /** The latest brief arrived in answer to the maker's note on the one before it. */
  briefRevisedOnNote: boolean
  /** The open interrupt was rehydrated on attach rather than streamed — it is not news, and the view
   * must not move for it. */
  interruptRecovered: boolean
  /** Questions answered by picking an option, for numbering the echo lines `Q1`, `Q2`, … The
   * harness numbers nothing and tags nothing, so the count is the only subject there is. */
  questionsAnswered: number
  /** The session's durable record, as the harness last sent it. Null until an owner read or
   * lifecycle frame supplies it. */
  record: HarnessSessionRecord | null
}

/** The notice code a run's handoff line carries: the branch the work was committed on. */
export const BRANCH_READY_NOTICE = 'branch_ready'

/** The notice code a plan adjustment's line carries. */
export const ADJUSTMENT_NOTICE = 'plan_adjusted'

/**
 * What a run has and a turn does not: the step it is running, whether it is still going, and what
 * stopped it. Kept beside the session state rather than inside it — the run happens on another
 * branch while this conversation stays open — and folded with it in `harness-session.reducer`.
 *
 * `appVersionId` is in it for one reason: a handoff is only this conversation's if it names the
 * session and the version that asked for the run, and that test belongs with the fold.
 */
export type HarnessRunState = {
  appVersionId: string | null
  /** The previewed step waiting to be run, cleared once the run starts. */
  preview: HarnessExecutionPreview | null
  /** A preview or a run is in flight. Apart from `streamState`, which is this conversation's. */
  executionBusy: boolean
  executionError: string | null
}

/** The state a run's frames fold over: the conversation that asked for it, and the run itself. */
export type HarnessRunFold = HarnessSessionState & HarnessRunState

/** Whether the durable plan record replaces its proposal for display. Brief confirmation
 * makes that record authoritative without answering the plan-review interrupt.
 * Explicit identities still protect a newer/different proposal from an older record. */
export function isPlanProposalSettled(
  record: HarnessSessionRecord | null,
  proposal: HarnessSessionState['storyPlanProposal']
): boolean {
  const plan = record?.plan
  if (
    !plan ||
    (proposal?.planId != null && proposal.planId !== plan.plan_id) ||
    (proposal?.version != null && proposal.version > plan.version)
  ) {
    return false
  }
  if (plan.confirmed) {
    return true
  }
  return (
    (!proposal || proposal.recordRevision < (record?.revision ?? 0)) &&
    (!!plan.execution || plan.state === 'COMPLETED' || plan.state === 'ABANDONED')
  )
}
